import { getCloudflareContext } from '@opennextjs/cloudflare';
// NVD API integration for fetching CVE data
// Uses the NIST National Vulnerability Database API

const NVD_API_BASE = 'https://services.nvd.nist.gov/rest/json/cves/2.0';

// NVD throttles anonymous callers (5 requests / 30s per IP) and Cloudflare
// Workers share egress IPs, so transient throttle/server responses are common
// on cache misses. Retry those (and network errors) with exponential backoff
// and jitter. Non-retryable statuses (e.g. 404 "not found") return immediately.
const NVD_RETRY_STATUSES = new Set([403, 429, 500, 502, 503, 504]);
const NVD_MAX_RETRIES = 2;

// An NVD API key raises the rate limit from 5 to 50 requests / 30s. It is read
// from the Cloudflare env (set as an encrypted secret, never committed). When
// absent — e.g. local dev — requests fall back to the anonymous limit.
function getNvdApiKey(): string | undefined {
  const candidates: Array<Record<string, unknown> | undefined> = [];
  try {
    candidates.push((globalThis as unknown as { cloudflare?: { env?: Record<string, unknown> } })?.cloudflare?.env);
  } catch { /* ignore */ }
  try {
    if (typeof getCloudflareContext === 'function') {
      candidates.push(getCloudflareContext()?.env as Record<string, unknown> | undefined);
    }
  } catch { /* ignore */ }
  try {
    candidates.push(process.env as unknown as Record<string, unknown>);
  } catch { /* ignore */ }

  for (const env of candidates) {
    const value = env?.NVD_API_KEY;
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return undefined;
}

async function fetchNVDWithRetry(url: string): Promise<Response> {
  const apiKey = getNvdApiKey();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (apiKey) headers.apiKey = apiKey;

  let lastError: unknown;
  for (let attempt = 0; attempt <= NVD_MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url, { headers });
      if (!NVD_RETRY_STATUSES.has(response.status) || attempt === NVD_MAX_RETRIES) {
        return response;
      }
    } catch (error) {
      lastError = error;
      if (attempt === NVD_MAX_RETRIES) throw error;
    }
    // Exponential backoff with jitter: ~0.4s, then ~0.8s (+ up to 250ms).
    const backoffMs = 400 * 2 ** attempt + Math.floor(Math.random() * 250);
    await new Promise((resolve) => setTimeout(resolve, backoffMs));
  }
  throw lastError ?? new Error('NVD request failed');
}

// NVD API response types
interface NVDWeaknessDescription {
  value: string;
  lang: string;
}

interface NVDReference {
  url: string;
  source: string;
  tags?: string[];
}

interface CVSSMetricV3 {
  cvssData: {
    baseScore: number;
    baseSeverity: string;
    vectorString: string;
    attackVector: string;
    attackComplexity: string;
    privilegesRequired: string;
    userInteraction: string;
    scope: string;
    confidentialityImpact: string;
    integrityImpact: string;
    availabilityImpact: string;
  };
}

interface CVSSMetricV2 {
  cvssData: {
    baseScore: number;
    vectorString: string;
    accessVector: string;
    accessComplexity: string;
    authentication: string;
    confidentialityImpact: string;
    integrityImpact: string;
    availabilityImpact: string;
  };
}

interface NVDVulnerabilityItem {
  cve: {
    id: string;
    descriptions?: Array<{ value: string; lang: string }>;
    published: string;
    lastModified: string;
    metrics?: {
      cvssMetricV31?: CVSSMetricV3[];
      cvssMetricV30?: CVSSMetricV3[];
      cvssMetricV2?: CVSSMetricV2[];
    };
    weaknesses?: Array<{
      description?: NVDWeaknessDescription[];
    }>;
    references?: NVDReference[];
    configurations?: unknown;
  };
}

export interface CVEData {
  id: string;
  description: string;
  published: string;
  lastModified: string;
  cvssV3?: {
    baseScore: number;
    baseSeverity: string;
    vectorString: string;
    attackVector: string;
    attackComplexity: string;
    privilegesRequired: string;
    userInteraction: string;
    scope: string;
    confidentialityImpact: string;
    integrityImpact: string;
    availabilityImpact: string;
  };
  cvssV2?: {
    baseScore: number;
    vectorString: string;
    accessVector: string;
    accessComplexity: string;
    authentication: string;
    confidentialityImpact: string;
    integrityImpact: string;
    availabilityImpact: string;
  };
  references: Array<{
    url: string;
    source: string;
    tags?: string[];
  }>;
  cwe?: string[];
  configurations?: unknown[];
  remediation?: string[];
  cweMitigations?: Array<{
    cweId: string;
    cweName: string;
    mitigations: Array<{
      phase: string[];
      description: string;
      effectiveness?: string;
      strategy?: string;
    }>;
  }>;
  // EPSS (Exploit Prediction Scoring System) data from FIRST.org
  epss?: {
    score: number; // Probability of exploitation (0-1)
    percentile: number; // Percentile ranking (0-1)
    date: string; // Date of EPSS calculation
  };
}

export interface SearchFilters {
  keyword?: string;
  cvssV3Severity?: string;
  pubStartDate?: string;
  pubEndDate?: string;
  lastModStartDate?: string;
  lastModEndDate?: string;
  cweId?: string;
  resultsPerPage?: number;
  startIndex?: number;
}

export async function fetchCVE(cveId: string): Promise<CVEData | null> {
  try {
    const response = await fetchNVDWithRetry(`${NVD_API_BASE}?cveId=${cveId}`);

    if (!response.ok) {
      throw new Error('Failed to fetch CVE data');
    }

    const data = await response.json();

    if (!data.vulnerabilities || data.vulnerabilities.length === 0) {
      return null;
    }

    const vuln = data.vulnerabilities[0].cve;

    // Extract CVSS v3.1 data
    let cvssV3;
    const cvssV3Data = vuln.metrics?.cvssMetricV31?.[0] || vuln.metrics?.cvssMetricV30?.[0];
    if (cvssV3Data) {
      const cvssData = cvssV3Data.cvssData;
      cvssV3 = {
        baseScore: cvssData.baseScore,
        baseSeverity: cvssData.baseSeverity,
        vectorString: cvssData.vectorString,
        attackVector: cvssData.attackVector,
        attackComplexity: cvssData.attackComplexity,
        privilegesRequired: cvssData.privilegesRequired,
        userInteraction: cvssData.userInteraction,
        scope: cvssData.scope,
        confidentialityImpact: cvssData.confidentialityImpact,
        integrityImpact: cvssData.integrityImpact,
        availabilityImpact: cvssData.availabilityImpact,
      };
    }

    // Extract CVSS v2 data
    let cvssV2;
    const cvssV2Data = vuln.metrics?.cvssMetricV2?.[0];
    if (cvssV2Data) {
      const cvssData = cvssV2Data.cvssData;
      cvssV2 = {
        baseScore: cvssData.baseScore,
        vectorString: cvssData.vectorString,
        accessVector: cvssData.accessVector,
        accessComplexity: cvssData.accessComplexity,
        authentication: cvssData.authentication,
        confidentialityImpact: cvssData.confidentialityImpact,
        integrityImpact: cvssData.integrityImpact,
        availabilityImpact: cvssData.availabilityImpact,
      };
    }

    // Extract CWE data
    const cwe = vuln.weaknesses?.[0]?.description?.map((d: NVDWeaknessDescription) => d.value) || [];

    // Extract references
    const references = vuln.references?.map((ref: NVDReference) => ({
      url: ref.url,
      source: ref.source,
      tags: ref.tags,
    })) || [];

    // Extract remediation information from references with Patch/Vendor Advisory tags
    const remediation: string[] = [];
    references.forEach((ref: { url: string; source: string; tags?: string[] }) => {
      if (ref.tags?.some((tag: string) =>
        ['Patch', 'Vendor Advisory', 'Mitigation'].includes(tag)
      )) {
        remediation.push(ref.url);
      }
    });

    return {
      id: vuln.id,
      description: vuln.descriptions?.[0]?.value || 'No description available',
      published: vuln.published,
      lastModified: vuln.lastModified,
      cvssV3,
      cvssV2,
      references,
      cwe,
      configurations: vuln.configurations,
      remediation: remediation.length > 0 ? remediation : undefined,
    };
  } catch (error) {
    console.error('Error fetching CVE:', error);
    return null;
  }
}

export function getSeverityColor(severity: string): string {
  switch (severity?.toUpperCase()) {
    case 'CRITICAL':
      return 'bg-red-600 text-white';
    case 'HIGH':
      return 'bg-orange-600 text-white';
    case 'MEDIUM':
      return 'bg-yellow-700 text-white';
    case 'LOW':
      return 'bg-blue-700 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
}

export function getSeverityFromScore(score: number): string {
  if (score >= 9.0) return 'CRITICAL';
  if (score >= 7.0) return 'HIGH';
  if (score >= 4.0) return 'MEDIUM';
  if (score > 0) return 'LOW';
  return 'NONE';
}

export async function searchCVEs(filters: SearchFilters): Promise<{ cves: CVEData[], totalResults: number } | null> {
  try {
    const params = new URLSearchParams();

    if (filters.keyword) params.append('keywordSearch', filters.keyword);
    if (filters.cvssV3Severity) params.append('cvssV3Severity', filters.cvssV3Severity);
    if (filters.pubStartDate) params.append('pubStartDate', filters.pubStartDate);
    if (filters.pubEndDate) params.append('pubEndDate', filters.pubEndDate);
    if (filters.lastModStartDate) params.append('lastModStartDate', filters.lastModStartDate);
    if (filters.lastModEndDate) params.append('lastModEndDate', filters.lastModEndDate);
    if (filters.cweId) params.append('cweId', filters.cweId);

    params.append('resultsPerPage', (filters.resultsPerPage || 20).toString());
    params.append('startIndex', (filters.startIndex || 0).toString());

    const response = await fetchNVDWithRetry(`${NVD_API_BASE}?${params.toString()}`);

    if (!response.ok) {
      throw new Error('Failed to search CVEs');
    }

    const data = await response.json();

    if (!data.vulnerabilities || data.vulnerabilities.length === 0) {
      return { cves: [], totalResults: 0 };
    }

    const cves: CVEData[] = data.vulnerabilities.map((item: NVDVulnerabilityItem) => {
      const vuln = item.cve;

      // Extract CVSS v3 data
      let cvssV3;
      const cvssV3Data = vuln.metrics?.cvssMetricV31?.[0] || vuln.metrics?.cvssMetricV30?.[0];
      if (cvssV3Data) {
        const cvssData = cvssV3Data.cvssData;
        cvssV3 = {
          baseScore: cvssData.baseScore,
          baseSeverity: cvssData.baseSeverity,
          vectorString: cvssData.vectorString,
          attackVector: cvssData.attackVector,
          attackComplexity: cvssData.attackComplexity,
          privilegesRequired: cvssData.privilegesRequired,
          userInteraction: cvssData.userInteraction,
          scope: cvssData.scope,
          confidentialityImpact: cvssData.confidentialityImpact,
          integrityImpact: cvssData.integrityImpact,
          availabilityImpact: cvssData.availabilityImpact,
        };
      }

      // Extract CVSS v2 data
      let cvssV2;
      const cvssV2Data = vuln.metrics?.cvssMetricV2?.[0];
      if (cvssV2Data) {
        const cvssData = cvssV2Data.cvssData;
        cvssV2 = {
          baseScore: cvssData.baseScore,
          vectorString: cvssData.vectorString,
          accessVector: cvssData.accessVector,
          accessComplexity: cvssData.accessComplexity,
          authentication: cvssData.authentication,
          confidentialityImpact: cvssData.confidentialityImpact,
          integrityImpact: cvssData.integrityImpact,
          availabilityImpact: cvssData.availabilityImpact,
        };
      }

      const cwe = vuln.weaknesses?.[0]?.description?.map((d: NVDWeaknessDescription) => d.value) || [];
      const references = vuln.references?.map((ref: NVDReference) => ({
        url: ref.url,
        source: ref.source,
        tags: ref.tags,
      })) || [];

      // Extract remediation
      const remediation: string[] = [];
      references.forEach((ref: { url: string; source: string; tags?: string[] }) => {
        if (ref.tags?.some((tag: string) =>
          ['Patch', 'Vendor Advisory', 'Mitigation'].includes(tag)
        )) {
          remediation.push(ref.url);
        }
      });

      return {
        id: vuln.id,
        description: vuln.descriptions?.[0]?.value || 'No description available',
        published: vuln.published,
        lastModified: vuln.lastModified,
        cvssV3,
        cvssV2,
        references,
        cwe,
        configurations: vuln.configurations,
        remediation: remediation.length > 0 ? remediation : undefined,
      };
    });

    return {
      cves,
      totalResults: data.totalResults || cves.length,
    };
  } catch (error) {
    console.error('Error searching CVEs:', error);
    return null;
  }
}
